import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Droplets, Gem, Package, Search, Sparkles, Tag } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useSupabaseSession } from "@/hooks/use-supabase-session";
import { computeKarmaNumerology, parseBirthDate } from "@/lib/karma/numerology";
import { splitProductTagsAndMeta } from "@/lib/shop-product-meta";
import { getSupabaseConfig, getSupabaseStorageKeyFromDirectUrl, resilientFetch } from "@/lib/supabase/client";

type ShopProduct = {
  id: string;
  sku: string;
  name: string;
  description: string;
  category: "stone" | "oil" | "bundle" | "other";
  priceTry: number;
  discountedPriceTry?: number;
  active: boolean;
  tags: string[];
  imageUrl?: string;
  imageUrls?: string[];
};

type CatalogResponse = {
  ok: true;
  products: ShopProduct[];
};

async function fetchProductsRequest(): Promise<ShopProduct[]> {
  const res = await resilientFetch("/api/numeroloji/catalog");
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Ürünler alınamadı.");
  }
  const data = json as CatalogResponse;
  return Array.isArray(data?.products) ? data.products : [];
}

function readAccessTokenFromStorage() {
  try {
    const { directUrl } = getSupabaseConfig();
    const storageKey = getSupabaseStorageKeyFromDirectUrl(String(directUrl)) ?? "";
    if (!storageKey) return "";
    const raw = globalThis.localStorage?.getItem(storageKey) ?? "";
    const parsed = raw ? (JSON.parse(raw) as any) : null;
    return typeof parsed?.access_token === "string" ? parsed.access_token : "";
  } catch {
    return "";
  }
}

async function fetchMembershipSummary() {
  const token = readAccessTokenFromStorage();
  if (!token) return null;

  const res = await resilientFetch("/api/numeroloji/me/entitlements", {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!res.ok) return null;
  const payload = (await res.json().catch(() => null)) as any;
  return payload?.membership ?? null;
}

async function fetchProducts(): Promise<ShopProduct[]> {
  try {
    return await fetchProductsRequest();
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error ?? "");
    const isNetworkError = error instanceof TypeError || /failed to fetch/i.test(message);
    if (!isNetworkError) throw error;

    await new Promise((resolve) => window.setTimeout(resolve, 450));

    try {
      return await fetchProductsRequest();
    } catch (retryError) {
      const retryMessage = retryError instanceof Error ? retryError.message : String(retryError ?? "");
      const retryIsNetwork = retryError instanceof TypeError || /failed to fetch/i.test(retryMessage);
      if (retryIsNetwork) {
        return [];
      }
      throw retryError;
    }
  }
}

function formatTry(amount: number) {
  try {
    return new Intl.NumberFormat("tr-TR", {
      style: "currency",
      currency: "TRY",
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${Math.round(amount)} ₺`;
  }
}

function normalizeTag(value: string) {
  return value
    .trim()
    .toLocaleLowerCase("tr-TR")
    .replace(/\s+/g, "")
    .replace(/[ç]/g, "c")
    .replace(/[ğ]/g, "g")
    .replace(/[ı]/g, "i")
    .replace(/[ö]/g, "o")
    .replace(/[ş]/g, "s")
    .replace(/[ü]/g, "u")
    .replace(/[^0-9a-z]+/g, "");
}

function getEffectivePrice(product: ShopProduct) {
  return typeof product.discountedPriceTry === "number" && product.discountedPriceTry > 0
    ? product.discountedPriceTry
    : product.priceTry;
}

function normalizeSearchText(value: string) {
  return value
    .trim()
    .toLocaleLowerCase("tr-TR")
    .replace(/[ç]/g, "c")
    .replace(/[ğ]/g, "g")
    .replace(/[ı]/g, "i")
    .replace(/[ö]/g, "o")
    .replace(/[ş]/g, "s")
    .replace(/[ü]/g, "u");
}

function normalizeCategoryParent(value: string) {
  const key = normalizeSearchText(value).replace(/[^a-z0-9-]/g, "");
  if (["stone", "tas", "dogal-tas", "dogaltas"].includes(key)) return "stone";
  if (["oil", "yag", "ucucu-yag", "ucucuyag", "aroma-yag"].includes(key)) return "oil";
  if (["bundle", "paket", "set", "kombin"].includes(key)) return "bundle";
  if (["other", "diger"].includes(key)) return "other";
  return key || "other";
}

function getCategoryParts(category: string) {
  const parts = String(category ?? "")
    .split(/[/>]/)
    .map((part) => part.trim())
    .filter(Boolean);

  const parent = normalizeCategoryParent(parts[0] ?? "other");
  const child = parts.length > 1 ? parts.slice(1).join("/") : "";
  return { parent, child };
}

function categoryIcon(category: ShopProduct["category"]) {
  const { parent } = getCategoryParts(category);
  switch (parent) {
    case "stone":
      return Gem;
    case "oil":
      return Droplets;
    case "bundle":
      return Package;
    default:
      return Sparkles;
  }
}

function categoryLabel(category: string) {
  const rawParts = String(category ?? "")
    .split(/[/>]/)
    .map((part) => part.trim())
    .filter(Boolean);

  const { parent, child } = getCategoryParts(category);
  const parentLabel =
    parent === "stone" ? "Taş" : parent === "oil" ? "Yağ" : parent === "bundle" ? "Paket" : parent === "other" ? "Diğer" : parent;

  if (!child) return parentLabel;

  const childLabel = rawParts
    .slice(1)
    .map((part) => part.replace(/[-_]+/g, " "))
    .join(" / ");

  return childLabel ? `${parentLabel} / ${childLabel}` : parentLabel;
}

function getKarmicDebtTag(code: number) {
  return `karmikborc${code}`;
}

function getProductImages(product: ShopProduct) {
  return Array.from(new Set([...(product.imageUrls ?? []), product.imageUrl ?? ""].map((url) => String(url ?? "").trim()).filter(Boolean))).slice(0, 5);
}

export default function UserPanelMagaza() {
  const { user } = useSupabaseSession();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");

  const profileInput = useMemo(() => {
    const firstName = typeof user?.metadata?.first_name === "string" ? (user?.metadata?.first_name as string) : "";
    const lastName = typeof user?.metadata?.last_name === "string" ? (user?.metadata?.last_name as string) : "";
    const fullName = `${firstName} ${lastName}`.trim();

    const birthDay = typeof user?.metadata?.birth_day === "number" ? user?.metadata?.birth_day : Number(user?.metadata?.birth_day);
    const birthMonth =
      typeof user?.metadata?.birth_month === "number" ? user?.metadata?.birth_month : Number(user?.metadata?.birth_month);
    const birthYear = typeof user?.metadata?.birth_year === "number" ? user?.metadata?.birth_year : Number(user?.metadata?.birth_year);

    if (!fullName || !Number.isFinite(birthDay) || !Number.isFinite(birthMonth) || !Number.isFinite(birthYear)) return null;

    const birthDate = parseBirthDate({
      day: birthDay,
      month: birthMonth,
      year: birthYear,
    });

    return {
      fullName,
      birthDate,
    };
  }, [user?.metadata?.birth_day, user?.metadata?.birth_month, user?.metadata?.birth_year, user?.metadata?.first_name, user?.metadata?.last_name]);

  const numerology = useMemo(() => {
    if (!profileInput) return null;
    return computeKarmaNumerology(profileInput.fullName, profileInput.birthDate);
  }, [profileInput]);

  const karmicCodes = numerology?.karmicCodes ?? [];

  const productsQuery = useQuery({
    queryKey: ["panel-shop-products"],
    queryFn: fetchProducts,
    staleTime: 30_000,
    retry: 1,
  });

  const entitlementsQuery = useQuery({
    queryKey: ["numeroloji-membership-summary", user?.id],
    queryFn: fetchMembershipSummary,
    enabled: Boolean(user?.id),
    staleTime: 30_000,
    retry: false,
  });

  const hasMemberStoreDiscount = useMemo(() => {
    const raw = entitlementsQuery.data?.hasFeature;
    if (!raw || typeof raw !== "object") return false;
    return Boolean((raw as Record<string, unknown>).member_store_discount);
  }, [entitlementsQuery.data?.hasFeature]);

  const { personalized, general, shown } = useMemo(() => {
    const products = (productsQuery.data ?? []).filter((p) => p && p.active);

    const normalizedGeneralKeys = new Set(["genel", "general"].map(normalizeTag));
    const karmicWanted = new Set(karmicCodes.map((code) => getKarmicDebtTag(code)).map(normalizeTag));

    const personalizedMatches: ShopProduct[] = [];
    const generalMatches: ShopProduct[] = [];

    for (const p of products) {
      const { cleanTags } = splitProductTagsAndMeta(p.tags ?? []);
      const tagKeys = new Set(cleanTags.filter(Boolean).map(normalizeTag));

      if (karmicWanted.size) {
        for (const wanted of karmicWanted) {
          if (tagKeys.has(wanted)) {
            personalizedMatches.push(p);
            break;
          }
        }
      }

      if (!karmicWanted.size) {
        for (const generalKey of normalizedGeneralKeys) {
          if (tagKeys.has(generalKey)) {
            generalMatches.push(p);
            break;
          }
        }
      }
    }

    const fallbackShown = karmicWanted.size ? personalizedMatches : generalMatches;

    return {
      personalized: personalizedMatches,
      general: generalMatches,
      shown: fallbackShown,
    };
  }, [karmicCodes, productsQuery.data]);

  const categoryOptions = useMemo(() => {
    const categories = Array.from(new Set(shown.map((p) => String(p.category ?? "").trim()).filter(Boolean)));
    categories.sort((a, b) => categoryLabel(a).localeCompare(categoryLabel(b), "tr"));
    return categories;
  }, [shown]);

  const filteredShown = useMemo(() => {
    const q = normalizeSearchText(search);
    return shown.filter((p) => {
      if (selectedCategory !== "all" && String(p.category ?? "").trim() !== selectedCategory) return false;
      if (!q) return true;
      const { cleanTags } = splitProductTagsAndMeta(p.tags ?? []);
      const hay = normalizeSearchText(`${p.name} ${p.description} ${p.category} ${categoryLabel(p.category)} ${cleanTags.join(" ")}`);
      return hay.includes(q);
    });
  }, [search, selectedCategory, shown]);

  const goToCheckout = (productId: string) => {
    const params = new URLSearchParams();
    params.set("productIds", productId);
    params.set("from", "panel-magaza");
    navigate(`/numeroloji/satin-al?${params.toString()}`);
  };

  const headerSubtitle = useMemo(() => {
    if (!karmicCodes.length) return "Karmik borcunuz yoksa ‘genel’ etiketli ürünleri görürsünüz.";
    return `Karmik borç kodlarınıza göre seçilmiş ürünler: ${karmicCodes.join(", ")}`;
  }, [karmicCodes]);

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-8">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div className="max-w-2xl">
          <h1 className="text-3xl font-black tracking-tight text-slate-900 dark:text-white">Kişiye Özel Mağaza</h1>
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">{headerSubtitle}</p>
          {hasMemberStoreDiscount ? (
            <p className="mt-2 text-xs font-semibold text-[var(--panel-primary)]">Üyelik indirimi aktif: sepette ek indirim uygulanır.</p>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-600 shadow-sm dark:border-[var(--panel-primary)]/10 dark:bg-slate-900 dark:text-slate-300">
            <Tag className="h-4 w-4 text-[var(--panel-primary)]" />
            {karmicCodes.length ? (
              <span>Karmik Borç: {karmicCodes.join(", ")}</span>
            ) : (
              <span>Genel öneriler</span>
            )}
          </div>
        </div>
      </div>

      {productsQuery.isLoading ? (
        <div className="text-sm text-slate-500 dark:text-slate-400">Ürünler yükleniyor...</div>
      ) : productsQuery.isError ? (
        <div className="text-sm text-red-600">{productsQuery.error instanceof Error ? productsQuery.error.message : "Ürünler alınamadı."}</div>
      ) : null}

      {!productsQuery.isLoading && !productsQuery.isError ? (
        shown.length ? (
          <>
            <div className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 dark:border-[var(--panel-primary)]/10 dark:bg-slate-900 sm:flex-row sm:items-center">
              <div className="relative flex-1">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Ürün, etiket veya kategori ara..."
                  className="h-11 w-full rounded-xl border border-slate-200 bg-white pl-10 pr-3 text-sm text-slate-900 outline-none ring-blue-600/20 focus:ring-4 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-100"
                />
              </div>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900 outline-none ring-blue-600/20 focus:ring-4 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-100"
              >
                <option value="all">Tüm kategoriler</option>
                {categoryOptions.map((cat) => (
                  <option key={cat} value={cat}>
                    {categoryLabel(cat)}
                  </option>
                ))}
              </select>
            </div>

            {filteredShown.length ? (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {filteredShown.map((p) => {
              const Icon = categoryIcon(p.category);
              const badge = karmicCodes.length ? "Karmik Borç Uyumlu" : "Genel";
              const basePrice = getEffectivePrice(p);
              const membershipDiscountedPrice = hasMemberStoreDiscount ? Math.max(1, Math.round(basePrice * 0.9)) : basePrice;

              return (
                <div
                  key={p.id}
                  className="group flex flex-col overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-sm transition-all hover:shadow-xl hover:shadow-[color:rgba(55,19,236,0.08)] dark:border-[var(--panel-primary)]/10 dark:bg-slate-900"
                >
                  <div className="relative aspect-square overflow-hidden bg-gradient-to-br from-[var(--panel-primary)]/15 to-slate-200 dark:to-slate-800">
                    {getProductImages(p)[0] ? (
                      <img
                        src={getProductImages(p)[0]}
                        alt={p.name}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    ) : null}

                    <div className="absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100 [background:radial-gradient(circle_at_35%_35%,rgba(55,19,236,0.35)_0%,transparent_60%)]" />

                    <div className="absolute left-4 top-4 inline-flex items-center gap-2 rounded-full bg-[var(--panel-primary)]/90 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-white backdrop-blur-md">
                      <Icon className="h-4 w-4" />
                      <span>{badge}</span>
                    </div>
                  </div>

                  <div className="flex flex-1 flex-col p-5">
                    <div className="text-lg font-extrabold text-slate-900 dark:text-white">{p.name}</div>
                    <div className="mt-2 line-clamp-3 text-xs leading-relaxed text-slate-500 dark:text-slate-400">{p.description}</div>

                    <div className="mt-3 inline-flex rounded-full bg-slate-100 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                      {categoryLabel(p.category)}
                    </div>

                    <div className="mt-4 flex flex-wrap gap-2">
                      {splitProductTagsAndMeta(p.tags ?? []).cleanTags.slice(0, 3).map((t, idx) => (
                        <span
                          key={`${p.id}-tag-${idx}`}
                          className="rounded-full bg-[var(--panel-primary)]/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-[var(--panel-primary)]"
                        >
                          {t}
                        </span>
                      ))}
                    </div>

                    <div className="mt-auto border-t border-slate-50 pt-4 dark:border-[var(--panel-primary)]/10">
                      <span className="flex items-end gap-2 text-lg font-black text-slate-900 dark:text-white">
                        {(typeof p.discountedPriceTry === "number" && p.discountedPriceTry > 0 && p.discountedPriceTry < p.priceTry) || hasMemberStoreDiscount ? (
                          <span className="text-xs font-semibold opacity-60 line-through">{formatTry(p.priceTry)}</span>
                        ) : null}
                        <span>{formatTry(membershipDiscountedPrice)}</span>
                      </span>

                      <div className="mt-3 grid grid-cols-2 gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-lg"
                          onClick={() => navigate(`/panel/magaza/urun/${encodeURIComponent(p.id)}`)}
                        >
                          İncele
                        </Button>
                        <Button size="sm" className="rounded-lg" onClick={() => goToCheckout(p.id)}>
                          Satın Al
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
            ) : (
              <Card className="border-slate-200 bg-white p-6 text-sm text-slate-600 dark:border-[var(--panel-primary)]/10 dark:bg-slate-900 dark:text-slate-300">
                Arama veya kategori filtresiyle eşleşen ürün bulunamadı.
              </Card>
            )}
          </>
        ) : (
          <Card className="border-slate-200 bg-white p-6 text-sm text-slate-600 dark:border-[var(--panel-primary)]/10 dark:bg-slate-900 dark:text-slate-300">
            {karmicCodes.length ? (
              <div>
                Karmik borç etiketlerinizle eşleşen ürün bulunamadı.
                {general.length ? " Genel etiketli ürünler yakında gösterilecek." : null}
              </div>
            ) : (
              <div>Genel etiketli ürün bulunamadı. Ürünlere “genel” etiketi ekleyebilirsiniz.</div>
            )}
          </Card>
        )
      ) : null}

      {karmicCodes.length && personalized.length ? (
        <Card className="border-slate-200 bg-white p-6 text-sm text-slate-600 dark:border-[var(--panel-primary)]/10 dark:bg-slate-900 dark:text-slate-300">
          <div className="font-semibold text-slate-900 dark:text-slate-100">Not</div>
          <div className="mt-1">
            Ürünlerin görünmesi için ürün yönetiminde etiketler alanına “Karmik Borç 1” gibi etiketler ekleyin. Sistem yazımı
            boşluk/büyük-küçük harf farklarına karşı esnektir.
          </div>
        </Card>
      ) : null}

    </div>
  );
}
